import React, { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import tw from "tailwind-styled-components";
import { MdClose, MdOutlineInfo, MdPhone } from "react-icons/md";
import { useSelector } from "react-redux";
import axios from "axios";
import Config from "../../Config";
import { useMutation, useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import AddDiscount from "../AddTenant/AddDiscount";
import * as Yup from "yup";
import { Field, Formik, Form } from "formik";
import { ActionSection, CardWrapper, CombineInputGroup, FieldWrappers, InputGroup, Label, ModalHeading, ModalSubHeading, Note, RupeeIcon, ScrollableContent, SelectionCard, SideCancelBtn, SubmitBtn, Wrapper } from "../Styles/InputStyles";
import DatePicker from "react-datepicker";
import moment from "moment";
import { FaRegCalendar } from "react-icons/fa";
import { FormControl, MenuItem, Select } from "@mui/material";
import Loading from "../Loading";
import { UserInfo } from "../Styles/PageStyles";

const OnboardingRequestModal = ({ setOnboardingRequestAccept, onboardingRequestAccept, selectedData, onboardingRequestMutate, onboardingRequestLoading }) => {
    const user = useSelector((state) => state.UserReducer.user);
    const [height, setHeight] = useState(window.innerHeight);

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight); // updates when Safari UI changes
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);
    const roomId = selectedData?.roomId;
    const propId = selectedData?.propertyId;
    const query = `?propId=${propId}&roomId=${roomId}`;
    const fetchListing = async () =>
        await axios.get(`${Config.apiUrl}/property/info/${query}`, {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
            },
        });

    const { isLoading, error, data, refetch, isRefetching } = useQuery(
        ['propertyData', propId, roomId],
        fetchListing,
        {
            enabled: onboardingRequestAccept,
        }
    );
    const rent = data?.data?.data?.room?.rent;
    const security = data?.data?.data?.property?.security;
    const [showAddDiscount, setShowAddDiscount] = useState(false);
    const [openCalendar, setOpenCalendar] = useState(null);
    const [addDiscountData, setAddDiscountData] = useState(null);
    const [discountedRent, setDiscountedRent] = useState(0);
    const [showDiscountedRent, setShowDiscountedRent] = useState(false);
    const allowedRentalCycles = ["01", "15", "00"];
    const initialValues = React.useMemo(() => ({
        fullName: selectedData?.tenantName || "",
        mobile: selectedData?.tenantMobile || "",
        monthlyRent: rent,
        securityDeposit: security,
        moveInDate: "",
        rentalCycle: allowedRentalCycles.includes(data?.data?.data?.property?.rentalCycle)
            ? data?.data?.data?.property?.rentalCycle
            : "01",
        agreementPeriod: data?.data?.data?.property?.agreementPeriod,
        noticePeriod: data?.data?.data?.property?.noticePeriod,
        lockInPeriod: data?.data?.data?.property?.lockInPeriod,
        agreementStartDate: "",
        electricityReading: 0,
        requestId: selectedData?.id,
    }), [data, rent, security, selectedData]);

    const monthlyRentRef = useRef(rent);
    const rentalCycleOptions = [
        { label: "1st to 1st", value: "01" },
        { label: "15th to 15th", value: "15" },
        { label: "Move-In Date", value: "00" },
    ];
    const calculateDiscountedRent = useCallback((monthlyRent, discountData) => {
        if (!discountData) return 0;

        const { discountType, discount } = discountData;
        let discountValue = 0;

        if (discountType === 1) {
            discountValue = Number(discount);
        } else {
            discountValue = (Number(monthlyRent) * Number(discount)) / 100;
        }

        return discountValue;
    }, []);
    const handleSubmit = (values) => {
        const nameRegex = /^[A-Za-z0-9. ]{4,}$/;
        const mobileRegex = /^\d{10}$/;
        const monthlyRentRegex = values.rentalType === 2 ? /^\d{1,6}$/ : /^(?!0+$)\d{4,6}$/;
        const maxMonthlyRent = 1000000;
        const securityDepositRegex = /^\d{1,6}$/;
        const maxSecurityDeposit = 1000000;

        if (!nameRegex.test(values.fullName)) {
            toast.error("Only letters, numbers, spaces & dots are allowed.");
            return;
        }

        if (!mobileRegex.test(values.mobile)) {
            toast.error("Mobile number should be exactly 10 digits long and contain only numbers");
            return;
        }

        if (
            !monthlyRentRegex.test(values.monthlyRent) ||
            parseInt(values.monthlyRent) > maxMonthlyRent
        ) {
            toast.error(
                "Monthly rent must be between 1000 and 10,00,000 and contain only numbers"
            );
            return;
        }
        if (
            (values.securityDeposit &&
                !securityDepositRegex.test(values.securityDeposit)) ||
            parseInt(values.securityDeposit) > maxSecurityDeposit
        ) {
            toast.error(
                "Security deposit must be between 1 and 10,00,000 and contain only numbers"
            );
            return;
        }
        onboardingRequestMutate({
            requestId: selectedData?.id,
            agreementStartDate: values.moveInDate,
            monthlyRent: values.monthlyRent,
            securityDeposit: values.securityDeposit || 0,
            rentalCycle: String(values.rentalCycle),
            agreementPeriod: values.agreementPeriod,
            noticePeriod: values.noticePeriod,
            lockInPeriod: values.lockInPeriod,
            moveInDate: values.moveInDate,
            isOnlinePayment: 0,
            allBedsOccupied: false,
            electricityReading: values.electricityReading,

        });

    };

    const validationSchema = Yup.object({
        fullName: Yup.string()
            .required('Name is required')
            .matches(/^[A-Za-z0-9. ]{4,}$/, 'Only letters, numbers, dot and spaces allowed. Minimum 4 characters.'),

        mobile: Yup.string()
            .required('Mobile number is required')
            .matches(/^\d{10}$/, 'Enter a valid 10-digit mobile number'),

        monthlyRent: Yup.number()
            .required("Rent is required")
            .test(
                'rent-validation',
                'Invalid rent amount',
                function (value) {

                    return value >= 1000 && value <= 1000000;
                }
            ),

        securityDeposit: Yup.number()
            .min(0, 'Security deposit cannot be negative')
            .max(1000000, 'Security deposit cannot exceed ₹10,00,000'),

        moveInDate: Yup.date()
            .required("Move-in date is required"),
        rentalCycle: Yup.string()
            .test(
                'rental-cycle-required',
                'Rental cycle is required',
                function (value) {
                    return ![1, 3, 6, 12].includes(this.parent.rentalType) || Boolean(value);
                }
            )
            .test(
                'valid-rental-cycle',
                'Invalid rental cycle',
                function (value) {
                    return ![1, 3, 6, 12].includes(this.parent.rentalType) || ["01", "15", "00"].includes(value);
                }
            ),

        agreementPeriod: Yup.number()
            .test(
                'agreement-period-required',
                'Agreement period is required',
                function (value) {
                    return ![1, 3, 6, 12].includes(this.parent.rentalType) || Boolean(value);
                }
            )
            .test(
                'valid-agreement-period',
                'Agreement period must be between 1 and 60 months',
                function (value) {
                    return ![1, 3, 6, 12].includes(this.parent.rentalType) ||
                        (value >= 1 && value <= 60);
                }
            ),

        noticePeriod: Yup.number()
            .test(
                'notice-period-required',
                'Notice period is required',
                function (value) {
                    return ![1, 3, 6, 12].includes(this.parent.rentalType) || Boolean(value);
                }
            )
            .test(
                'valid-notice-period',
                'Notice period must be between 1 and 12 months',
                function (value) {
                    return ![1, 3, 6, 12].includes(this.parent.rentalType) ||
                        (value >= 1 && value <= 12);
                }
            ),

        lockInPeriod: Yup.number()
            .test(
                'lockin-period-required',
                'Lock-in period is required',
                function (value) {
                    return ![1, 3, 6, 12].includes(this.parent.rentalType) || Boolean(value);
                }
            )
            .test(
                'valid-lockin-period',
                'Lock-in period must be between 1 and 12 months',
                function (value) {
                    return ![1, 3, 6, 12].includes(this.parent.rentalType) ||
                        (value >= 1 && value <= 12);
                }
            )
            .test(
                'lockin-less-than-agreement',
                'Lock-in period cannot exceed agreement period',
                function (value) {
                    return ![1, 3, 6, 12].includes(this.parent.rentalType) ||
                        !value ||
                        !this.parent.agreementPeriod ||
                        value <= this.parent.agreementPeriod;
                }
            )

    });
    //------- Add Discount -------

    const AddDiscountFunction = async (values) => {
        return await axios.post(`${Config.apiUrl}/discount/add`, values, {
            headers: {
                authorization: "Bearer" + " " + user.token,
                "Content-Type": "application/json",
            },
        });
    };
    const onAddDiscountSuccess = (data, variables) => {
        if (data?.data?.isSuccess === true) {
            // toast.success(data?.data?.msg || "Success");
            setAddDiscountData(variables)
            setShowAddDiscount(false)
            setShowDiscountedRent(true)
            const newDiscountedRent = calculateDiscountedRent(monthlyRentRef.current, variables);
            setDiscountedRent(newDiscountedRent);
        } else {
            toast.error(data?.data?.msg || "An Error Occured");
        }
    };

    const onAddDiscountError = (data) => {
        toast.error(data?.response?.data?.msg || "An Error Occured");
    };

    const { isLoading: addDiscountLoading, mutate: addDiscountMutate } =
        useMutation(AddDiscountFunction, {
            onSuccess: onAddDiscountSuccess,
            onError: onAddDiscountError,
        });

    useEffect(() => {
        if (addDiscountData && showDiscountedRent) {
            const newDiscountedRent = calculateDiscountedRent(monthlyRentRef.current, addDiscountData);
            setDiscountedRent(newDiscountedRent);
        }
    }, [addDiscountData, showDiscountedRent, calculateDiscountedRent]);
    //------- Remove Discount -------

    const RemoveDiscountFunction = async (payload) => {
        return await axios.post(`${Config.apiUrl}/discount/remove`, payload, {
            headers: {
                authorization: "Bearer" + " " + user.token,
                "Content-Type": "application/json",
            },
        });
    };

    const onRemoveDiscountSuccess = (data) => {
        if (data?.data?.isSuccess === true) {
            toast.success(data?.data?.msg || "Discount removed successfully");
            setAddDiscountData(null);
            setShowDiscountedRent(false);
            setDiscountedRent(0);
        } else {
            toast.error(data?.data?.msg || "An Error Occured");
        }
    };

    const onRemoveDiscountError = (data) => {
        toast.error(data?.response?.data?.msg || "An Error Occured");
    };

    const { isLoading: removeDiscountLoading, mutate: removeDiscountMutate } =
        useMutation(RemoveDiscountFunction, {
            onSuccess: onRemoveDiscountSuccess,
            onError: onRemoveDiscountError,
        });

    const handleDiscountClose = () => {
        setShowAddDiscount(false);

    };
    const handleRemoveDiscount = () => {
        const payload = {
            tenantId: selectedData?.tenantId,
        };
        removeDiscountMutate(payload);
    };
    const onClose = () => {
        setOnboardingRequestAccept(false);
    };

    return (
        <Container style={{ height: `${height}px`, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <Header>
                <h1 className="text-white text-lg font-medium">Approve {selectedData?.tenantName}</h1>
                <button onClick={onClose}>
                    <MdClose color="white" size={24} />
                </button>
            </Header>
            <ProfileSection className="flex justify-between">
                <UserInfo>
                    <span className="font-medium mb-0">{selectedData?.tenantName}</span>
                    <span className="text-sm text-gray-600">
                        #Room {selectedData?.roomNum}, {selectedData?.flatName}, {selectedData?.propertyName}
                    </span>
                </UserInfo>
                <div className="flex gap-2 items-center text-gray-600">
                    <MdPhone className="text-gray-400" size={14} />
                    <span className="text-sm">{selectedData?.tenantMobile}</span>
                </div>
            </ProfileSection>
            <div className="px-6 h-full flex flex-col justify-between">
                {(isLoading || isRefetching) && !data && <Loading />}
                {!error && !isLoading && data && (
                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                        enableReinitialize
                    >

                        {({ values, errors, setFieldValue, touched }) => {
                            monthlyRentRef.current = values.monthlyRent;
                            if (addDiscountData && showDiscountedRent) {
                                const newDiscountedRent = calculateDiscountedRent(values.monthlyRent, addDiscountData);
                                if (newDiscountedRent !== discountedRent) {
                                    setDiscountedRent(newDiscountedRent);
                                }
                            } return (
                                <>
                                    <div
                                        className={`${showAddDiscount ? "flex" : "hidden"} h-screen w-screen bg-black/30 backdrop-blur-sm fixed inset-0 transition-all duration-400 z-10 ease-in-out`}
                                    />
                                    <div
                                        className={`${showAddDiscount ? "translate-x-0" : "translate-x-full"} fixed h-screen right-0 top-0 transition-all duration-300 ease-in-out bg-white flex items-start rounded-l-2xl overflow-hidden w-full  justify-start z-50`}
                                    >
                                        {showAddDiscount && (
                                            <AddDiscount
                                                handleClose={handleDiscountClose}
                                                tenantId={selectedData?.tenantId}
                                                monthlyRent={values.monthlyRent}
                                                addDiscountLoading={addDiscountLoading}
                                                addDiscountMutate={addDiscountMutate}
                                            />
                                        )}
                                    </div>
                                    <Form>
                                        <ScrollableContent>
                                            {/* Step 1: Basic Details */}
                                            <StepContainer className="mt-4">
                                                <StepHeader>
                                                    <ModalSubHeading className="!mt-0">Step 1</ModalSubHeading>
                                                    <ModalHeading>Basic Details</ModalHeading>
                                                </StepHeader>
                                                <StepContent>
                                                    <Wrapper className="!mt-0">
                                                        <InputGroup>
                                                            <Label htmlFor="fullName">Name *</Label>
                                                            <FieldWrappers className="bg-gray-100">
                                                                <Field
                                                                    type="text"
                                                                    name="fullName"
                                                                    id="fullName"
                                                                    autoComplete="off"
                                                                    disabled

                                                                />
                                                            </FieldWrappers>
                                                            {touched.fullName && errors.fullName && (
                                                                <p className="text-rose-600 text-sm">
                                                                    {errors.fullName}
                                                                </p>
                                                            )}
                                                        </InputGroup>

                                                        <InputGroup>
                                                            <Label htmlFor="mobile">Mobile *</Label>
                                                            <FieldWrappers className="bg-gray-100">
                                                                <Field
                                                                    type="text"
                                                                    name="mobile"
                                                                    id="mobile"
                                                                    inputMode="numeric"
                                                                    autoComplete="off"
                                                                    maxLength={10}
                                                                    disabled

                                                                />
                                                            </FieldWrappers>
                                                            {touched.mobile && errors.mobile && (
                                                                <p className="text-rose-600 text-sm">
                                                                    {errors.mobile}
                                                                </p>
                                                            )}
                                                        </InputGroup>
                                                    </Wrapper>
                                                </StepContent>
                                            </StepContainer>

                                            {/* Step 2: Rent Details */}
                                            <StepContainer>
                                                <StepHeader>
                                                    <ModalSubHeading className="!mt-0">Step 2</ModalSubHeading>
                                                    <ModalHeading>Rent Details</ModalHeading>
                                                </StepHeader>
                                                <StepContent>
                                                    <Wrapper className="!mt-0">
                                                        <InputGroup>
                                                            <Label>Rental Cycle *</Label>
                                                            <CardWrapper className="flex-wrap">
                                                                {rentalCycleOptions.map((mode) => (
                                                                    <SelectionCard
                                                                        key={mode.value}
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            setFieldValue("rentalCycle", mode.value, false);
                                                                        }}
                                                                        className={values.rentalCycle === mode.value
                                                                            ? "bg-[#26A69A] text-white px-4 lg:px-6"
                                                                            : "bg-gray-100 text-gray-600 px-4 lg:px-6"}
                                                                    >
                                                                        <span className="text-sm">{mode.label}</span>
                                                                    </SelectionCard>
                                                                ))}
                                                            </CardWrapper>
                                                        </InputGroup>
                                                        <Note className="-mt-1">
                                                            <MdOutlineInfo className="mt-0.5 mr-1" />
                                                            {values.rentalCycle === "01"
                                                                ? "Rent due will be generated on the 1st of each month."
                                                                : values.rentalCycle === "15"
                                                                    ? "Rent due will be generated on the 15th of each month."
                                                                    : "Rent due will be generated each month based on move-in date."}
                                                        </Note>

                                                        <InputGroup>
                                                            <Label htmlFor="monthlyRent">
                                                                Monthly Rent *
                                                            </Label>
                                                            <FieldWrappers>
                                                                <RupeeIcon />
                                                                <Field
                                                                    type="number"
                                                                    name="monthlyRent"
                                                                    id="monthlyRent"
                                                                    autoComplete="off"
                                                                    required
                                                                    className="pl-6" />
                                                            </FieldWrappers>

                                                            {touched.monthlyRent && errors.monthlyRent && (
                                                                <p className="text-rose-600 text-sm">
                                                                    {errors.monthlyRent}
                                                                </p>
                                                            )}

                                                            <div className="flex">
                                                                {showDiscountedRent && (
                                                                    <div className="text-gray-800 mr-auto text-sm">
                                                                        Discounted Rent: ₹{Number(values.monthlyRent) - discountedRent}
                                                                    </div>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    className="text-blue-400 underline ml-auto text-sm"
                                                                    onClick={() => {
                                                                        if (showDiscountedRent) {
                                                                            handleRemoveDiscount();
                                                                        } else {
                                                                            setShowAddDiscount(true);
                                                                        }
                                                                    }}
                                                                >
                                                                    {showDiscountedRent ? 'Remove Discount' : 'Add Discount'}
                                                                </button>
                                                            </div>

                                                        </InputGroup>
                                                        <InputGroup>
                                                            <Label htmlFor="securityDeposit">Security Deposit </Label>
                                                            <FieldWrappers>
                                                                <RupeeIcon />
                                                                <Field
                                                                    type="number"
                                                                    name="securityDeposit"
                                                                    id="securityDeposit"
                                                                    autoComplete="off"
                                                                    className="pl-6" />
                                                            </FieldWrappers>
                                                            {touched.securityDeposit && errors.securityDeposit && (
                                                                <p className="text-rose-600 text-sm">
                                                                    {errors.securityDeposit}
                                                                </p>
                                                            )}
                                                        </InputGroup>
                                                    </Wrapper>
                                                </StepContent>
                                            </StepContainer>

                                            {/* Step 3: Stay Details */}
                                            <StepContainer>
                                                <StepHeader>
                                                    <ModalSubHeading className="!mt-0">Step 3</ModalSubHeading>
                                                    <ModalHeading>Stay Details</ModalHeading>
                                                </StepHeader>
                                                <StepContent>
                                                    <Wrapper className="!mt-0">
                                                        <InputGroup>
                                                            <Label htmlFor="moveInDate">
                                                                Move-In Date *
                                                            </Label>
                                                            <FieldWrappers>
                                                                <DatePicker
                                                                    selected={values.moveInDate ? new Date(values.moveInDate) : null}
                                                                    onChange={(date) => {
                                                                        setFieldValue("moveInDate", moment(date).format("YYYY-MM-DD"));
                                                                        setOpenCalendar(null);
                                                                    }}
                                                                    onClickOutside={() => setOpenCalendar(null)}
                                                                    onSelect={() => setOpenCalendar(null)}
                                                                    open={openCalendar === 'moveIn'}
                                                                    onInputClick={() => {
                                                                        setOpenCalendar(openCalendar === 'moveIn' ? null : 'moveIn');
                                                                    }}
                                                                    dateFormat="yyyy-MM-dd"
                                                                    className="cursor-pointer items-center"
                                                                    placeholderText="YYYY-MM-DD"
                                                                    showIcon
                                                                    icon={<FaRegCalendar className="text-gray-400 text-xs mt-0.5" />}
                                                                    showPopperArrow={false}
                                                                    onFocus={(e) => e.target.blur()}
                                                                    wrapperClassName="w-full"
                                                                    name="moveInDate"
                                                                    id="moveInDate"
                                                                    autoComplete="off"
                                                                    portalId="root-portal"
                                                                    popperClassName="react-datepicker-popper" />
                                                            </FieldWrappers>
                                                            {touched.moveInDate && errors.moveInDate && (
                                                                <p className="text-rose-600 text-sm">
                                                                    {errors.moveInDate}
                                                                </p>
                                                            )}
                                                        </InputGroup>
                                                        <InputGroup>
                                                            <Label htmlFor="agreementPeriod">Agreement Period *</Label>
                                                            <FieldWrappers>
                                                                <Field
                                                                    type="number"
                                                                    name="agreementPeriod"
                                                                    id="agreementPeriod"
                                                                    autoComplete="off"
                                                                    required />
                                                            </FieldWrappers>
                                                            {touched.agreementPeriod && errors.agreementPeriod && (
                                                                <p className="text-rose-600 text-sm">
                                                                    {errors.agreementPeriod}
                                                                </p>
                                                            )}
                                                        </InputGroup>
                                                        <CombineInputGroup>
                                                            <InputGroup>
                                                                <Label htmlFor="noticePeriod">Notice Period *</Label>
                                                                <FormControl fullWidth>
                                                                    <Select
                                                                        className="px-2"
                                                                        id="noticePeriod"
                                                                        name="noticePeriod"
                                                                        value={values.noticePeriod}
                                                                        onChange={(e) => setFieldValue("noticePeriod", e.target.value)}
                                                                        required
                                                                        style={{ height: "46px" }}
                                                                    >
                                                                        {Array.from({ length: 6 }, (_, i) => i + 1).map(
                                                                            (month) => (
                                                                                <MenuItem key={month} value={month}>
                                                                                    {month} {month === 1 ? "month" : "months"}
                                                                                </MenuItem>
                                                                            )
                                                                        )}
                                                                    </Select>
                                                                </FormControl>
                                                            </InputGroup>
                                                            <InputGroup>
                                                                <Label htmlFor="lockInPeriod">Lock-In Period *</Label>
                                                                <FormControl fullWidth>
                                                                    <Select
                                                                        className="px-2"
                                                                        id="lockInPeriod"
                                                                        name="lockInPeriod"
                                                                        value={values.lockInPeriod}
                                                                        onChange={(e) => setFieldValue("lockInPeriod", e.target.value)}
                                                                        required
                                                                        style={{ height: "46px" }}
                                                                    >
                                                                        {Array.from({ length: 11 }, (_, i) => i + 1).map(
                                                                            (month) => (
                                                                                <MenuItem key={month} value={month}>
                                                                                    {month} {month === 1 ? "month" : "months"}
                                                                                </MenuItem>
                                                                            )
                                                                        )}
                                                                    </Select>
                                                                </FormControl>
                                                            </InputGroup>
                                                        </CombineInputGroup>
                                                    </Wrapper>
                                                </StepContent>
                                            </StepContainer>
                                        </ScrollableContent>
                                        <ActionSection>
                                            <SideCancelBtn type="button" onClick={onClose}>
                                                Cancel
                                            </SideCancelBtn>
                                            <SubmitBtn type="submit" disabled={onboardingRequestLoading}>
                                                {onboardingRequestLoading ? "Loading..." : "Approve"}
                                            </SubmitBtn>
                                        </ActionSection>
                                    </Form>
                                </>
                            )
                        }}
                    </Formik>)}
            </div>
        </Container>
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const Header = tw.div`flex items-center justify-between bg-primary-color py-4 px-4`;
const ProfileSection = tw.div`flex items-center gap-3 bg-gray-100 px-4 p-4`;

// New styled components for step separation
const StepContainer = tw.div`mb-6 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden`;
const StepHeader = tw.div`bg-gray-50 px-4 py-3 border-b border-gray-200`;
const StepContent = tw.div`px-4 py-4`;

export default OnboardingRequestModal;